import {Button} from '@dropbox/dig-components/buttons';
import {Checkbox, RadioButton} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Tabs} from '@dropbox/dig-components/tabs';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, InfoLine, SearchLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {
  AiWorkstreamHealthScoreService,
  AiWorkstreamOutcomesValidationService,
  type WorkstreamBasic,
  type WorkstreamCheckResponse,
  type WorkstreamHealthScoreResponse,
  type WorkstreamHealthScoreTaskResponse,
  type WorkstreamOutcomesValidationResponse,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {isSuperUser, isUserAdmin} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {NotFound} from 'pages/NotFound';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router';
import {getBackendUrl, getFrontendUrl, getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';
import {useTimeframes, useTimeframesQuery} from 'views/workstreams/detail/hooks';
import {
  parseWorkstreamTimeframeFromSearchParam,
  type WorkstreamTimeframe,
} from 'views/workstreams/detail/workstreamTimeframe';
import {
  getChecksForDisplay,
  getFailureCopy,
  getWorkstreamCheckIdsForType,
  type WorkstreamCheckType,
} from 'views/workstreams/workstreamChecks';
import {
  HealthScoreChecks,
  HealthScoreIcon,
  HealthScoreResults,
  useWorkstreamHealthScoreContext,
  useWorkstreamHealthScoreGenerate,
  useWorkstreamHealthScores,
} from 'views/workstreams/WorkstreamHealthScore';

export const AdminWorkstreamReadiness = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const timeframeParam = searchParams.get('timeframe');
  const {timeframes, isLoading: isLoadingTimeframes} = useTimeframesQuery();
  const timeframeFromUrl = parseWorkstreamTimeframeFromSearchParam(timeframeParam, timeframes);
  // A `?timeframe=` can't be read until the timeframes load; showing All
  // timeframes meanwhile would load every score and then the filtered ones.
  const isWaitingForTimeframes =
    Boolean(timeframeParam) && !timeframes.length && isLoadingTimeframes;
  const [selectedTimeframe, setSelectedTimeframe] = useState<WorkstreamTimeframe | undefined>(
    timeframeFromUrl
  );
  const hasMountedRef = useRef(false);

  useEffect(() => {
    setSelectedTimeframe(timeframeFromUrl);
  }, [timeframeFromUrl]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    void queryClient.invalidateQueries({
      queryKey: ['workstream', 'health_scores', 'default', selectedTimeframe],
    });
  }, [selectedTimeframe]);

  const handleTimeframeChange = (nextTimeframe: WorkstreamTimeframe | undefined) => {
    setSelectedTimeframe(nextTimeframe);
    setSearchParams((currentSearchParams) => {
      const nextSearchParams = new URLSearchParams(currentSearchParams);

      if (nextTimeframe) {
        nextSearchParams.set('timeframe', nextTimeframe);
      } else {
        nextSearchParams.delete('timeframe');
      }

      return nextSearchParams;
    });
  };

  const pulseUser = useAtomValue(pulseUserAtom);
  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser) && !isSuperUser(pulseUser)) {
    return <NotFound />;
  }

  if (isWaitingForTimeframes) {
    return <Spinner />;
  }

  return (
    <WorkstreamReadinessDashboard
      timeframe={selectedTimeframe}
      onTimeframeChange={handleTimeframeChange}
    />
  );
};

const WorkstreamReadinessDashboard = ({
  timeframe,
  onTimeframeChange,
}: {
  timeframe: WorkstreamTimeframe | undefined;
  onTimeframeChange: (timeframe: WorkstreamTimeframe | undefined) => void;
}) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const canGenerateHealthScore = isSuperUser(pulseUser);

  const [selectedTab, setSelectedTab] = useState('all-health-scores');
  return (
    <Layout.Container>
      <Stack gap="8">
        <Title size={18}>Workstream Readiness Dashboard</Title>
        <Tabs selectedTab={selectedTab} onSelection={setSelectedTab}>
          <Box>
            <Tabs.Group>
              <Tabs.Tab id="all-health-scores">All Health Scores</Tabs.Tab>
              <Tabs.Tab id="all-outcome-validations">All Milestone Validations</Tabs.Tab>
              {canGenerateHealthScore && (
                <Tabs.Tab id="generate-health-score">Generate Health Score</Tabs.Tab>
              )}
            </Tabs.Group>
          </Box>
          <Box overflowY="auto">
            <Tabs.Panel tabId="all-health-scores">
              <TabContainer>
                <ViewHealthScores
                  timeframe={timeframe}
                  onTimeframeChange={onTimeframeChange}
                  canRegenerate={canGenerateHealthScore}
                />
              </TabContainer>
            </Tabs.Panel>
            <Tabs.Panel tabId="all-outcome-validations">
              <TabContainer>
                <ViewOutcomeValidations canRegenerate={canGenerateHealthScore} />
              </TabContainer>
            </Tabs.Panel>
            {canGenerateHealthScore && (
              <Tabs.Panel tabId="generate-health-score">
                <TabContainer>
                  <GenerateHealthScoreTool checksType="default" timeframe={timeframe} />
                </TabContainer>
              </Tabs.Panel>
            )}
          </Box>
        </Tabs>
      </Stack>
    </Layout.Container>
  );
};

const TabContainer = ({children}: {children: React.ReactNode}) => {
  return (
    <Box paddingTop="24" paddingBottom="24">
      {children}
    </Box>
  );
};

const OUTCOME_VALIDATION_TABLE_COLUMNS = [
  {type: 'Metric', minWidth: 330},
  {type: 'Metric Type', minWidth: 100},
  {type: 'Score', minWidth: 200},
  {type: 'Checks', minWidth: 370},
];

type OutcomeValidationScoreFilter = 'not_generated' | 'poor' | 'adequate' | 'exemplary';

const OUTCOME_VALIDATION_SCORE_FILTER_OPTIONS: Array<{
  label: string;
  value: OutcomeValidationScoreFilter;
}> = [
  {label: 'Poor', value: 'poor'},
  {label: 'Adequate', value: 'adequate'},
  {label: 'Exemplary', value: 'exemplary'},
  {label: 'Not generated', value: 'not_generated'},
];

const READINESS_REGENERATE_BATCH_SIZE = 50;
const READINESS_GENERATION_STATUS_POLL_INTERVAL_MS = 5000;

type RegenerationStatus = {
  requestedCount: number;
  queuedCount: number;
  completedCount: number;
  settledCount: number;
  failedToStartCount: number;
};

const getRegenerationStatusLabel = (status: RegenerationStatus | undefined) => {
  if (!status) {
    return undefined;
  }

  const failedCount =
    status.failedToStartCount + Math.max(0, status.settledCount - status.completedCount);
  if (failedCount > 0) {
    return `Regenerated ${status.completedCount} of ${status.requestedCount}, ${failedCount} failed`;
  }

  return `Regenerated ${status.completedCount} of ${status.requestedCount}`;
};

const getBulkRegenerateButtonLabel = (totalCount: number) =>
  totalCount > READINESS_REGENERATE_BATCH_SIZE
    ? `Regenerate next ${READINESS_REGENERATE_BATCH_SIZE} of ${totalCount}`
    : `Regenerate All (${totalCount})`;

const BulkRegenerateAction = ({
  totalCount,
  isRegenerating,
  status,
  onRegenerate,
  buttonLabel,
}: {
  totalCount: number;
  isRegenerating: boolean;
  status?: string;
  onRegenerate: () => void;
  buttonLabel?: string;
}) => (
  <>
    <Button
      variant="primary"
      onClick={onRegenerate}
      disabled={totalCount === 0 || isRegenerating}
      isLoading={isRegenerating}
    >
      {buttonLabel ?? getBulkRegenerateButtonLabel(totalCount)}
    </Button>
    {status && (
      <Text color="subtle" size="small">
        {status}
      </Text>
    )}
  </>
);

type OutcomeValidationGenerationTask = {
  taskId: string;
  metricId: number;
};

const getOutcomeValidationSelectionKey = (
  outcomesValidation: WorkstreamOutcomesValidationResponse
) =>
  outcomesValidation.task_id ?? `${outcomesValidation.metric_id}:${outcomesValidation.checks_type}`;

const getOutcomeValidationScoreLabel = (score?: string | null) => {
  if (score?.toLowerCase() === 'exemplary') {
    return t('outcomes_validation_all_criteria_met').toString();
  }

  if (score) {
    return t('outcomes_validation_criteria_not_met').toString();
  }

  return '—';
};

const getOutcomeValidationMetricTypeLabel = (metricType?: string | null) => {
  if (!metricType) {
    return '—';
  }
  return metricType === 'deliverable' ? 'milestone' : metricType;
};

const formatGeneratedAt = (generatedAt: string) =>
  new Date(generatedAt).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: undefined,
    hour: '2-digit',
    minute: '2-digit',
  });

const isValidOutcomeValidation = (outcomesValidation: WorkstreamOutcomesValidationResponse) => {
  const checksType = outcomesValidation.checks_type as WorkstreamCheckType;
  const isOutcomesValidationChecksType = checksType.startsWith('outcomes_validation_');
  const enabledCheckIds = isOutcomesValidationChecksType
    ? getWorkstreamCheckIdsForType(checksType)
    : [];
  const returnedCheckIds = new Set(outcomesValidation.checks?.map((check) => check.id));
  const hasAllEnabledChecks =
    enabledCheckIds.length > 0 && enabledCheckIds.every((checkId) => returnedCheckIds.has(checkId));

  return Boolean(
    outcomesValidation.metric_title.trim() &&
    outcomesValidation.metric_type?.trim() &&
    outcomesValidation.score?.trim() &&
    hasAllEnabledChecks
  );
};

const ViewOutcomeValidations = ({canRegenerate}: {canRegenerate: boolean}) => {
  const [scoreFilters, setScoreFilters] = useState<Set<OutcomeValidationScoreFilter>>(new Set());
  const [metricTypeFilters, setMetricTypeFilters] = useState<Set<string>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateStatus, setRegenerateStatus] = useState<RegenerationStatus | undefined>();
  const [selectedOutcomeValidationKeys, setSelectedOutcomeValidationKeys] = useState<Set<string>>(
    new Set()
  );
  const {data, isLoading} = useQuery({
    queryKey: ['workstream', 'outcomes_validations'],
    queryFn: () =>
      getService(
        AiWorkstreamOutcomesValidationService
      ).getWorkstreamOutcomesValidationsApiV1WorkstreamOutcomesValidationsGet(),
    retry: false,
  });
  const outcomesValidations = useMemo(() => data?.workstream_outcomes_validations ?? [], [data]);
  const metricTypeFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          outcomesValidations
            .map((outcomesValidation) => outcomesValidation.metric_type)
            .filter((metricType): metricType is string => Boolean(metricType))
        )
      )
        .sort()
        .map((metricType) => ({
          label: metricType.replace(
            /(^|_)([a-z])/g,
            (_, prefix, letter) => `${prefix === '_' ? ' ' : ''}${letter.toUpperCase()}`
          ),
          value: metricType,
        })),
    [outcomesValidations]
  );
  const filteredOutcomesValidations = outcomesValidations.filter((outcomesValidation) => {
    const isValid = isValidOutcomeValidation(outcomesValidation);
    const matchesScoreFilter =
      scoreFilters.size === 0 ||
      (!isValid && scoreFilters.has('not_generated')) ||
      (isValid &&
        scoreFilters.has(outcomesValidation.score?.toLowerCase() as OutcomeValidationScoreFilter));

    return (
      matchesScoreFilter &&
      (metricTypeFilters.size === 0 ||
        (outcomesValidation.metric_type !== null &&
          outcomesValidation.metric_type !== undefined &&
          metricTypeFilters.has(outcomesValidation.metric_type)))
    );
  });

  const handleToggleScore = (scoreFilter: OutcomeValidationScoreFilter) => {
    setScoreFilters((currentScoreFilters) => {
      const nextScoreFilters = new Set(currentScoreFilters);
      if (nextScoreFilters.has(scoreFilter)) {
        nextScoreFilters.delete(scoreFilter);
      } else {
        nextScoreFilters.add(scoreFilter);
      }
      return nextScoreFilters;
    });
  };

  const handleToggleMetricType = (metricType: string) => {
    setMetricTypeFilters((currentMetricTypeFilters) => {
      const nextMetricTypeFilters = new Set(currentMetricTypeFilters);
      if (nextMetricTypeFilters.has(metricType)) {
        nextMetricTypeFilters.delete(metricType);
      } else {
        nextMetricTypeFilters.add(metricType);
      }
      return nextMetricTypeFilters;
    });
  };

  const regenerableOutcomesValidations = filteredOutcomesValidations.filter(
    (outcomesValidation) => {
      const metricId = Number(outcomesValidation.metric_id);
      return Number.isInteger(metricId) && metricId > 0;
    }
  );
  const selectedOutcomesValidations = regenerableOutcomesValidations.filter((outcomesValidation) =>
    selectedOutcomeValidationKeys.has(getOutcomeValidationSelectionKey(outcomesValidation))
  );
  const outcomesValidationsToRegenerate = selectedOutcomesValidations.length
    ? selectedOutcomesValidations
    : regenerableOutcomesValidations;
  const regenerateButtonLabel = selectedOutcomesValidations.length
    ? `Regenerate selected (${selectedOutcomesValidations.length})`
    : undefined;

  const handleToggleOutcomeValidationSelection = (
    outcomesValidation: WorkstreamOutcomesValidationResponse
  ) => {
    const selectionKey = getOutcomeValidationSelectionKey(outcomesValidation);
    setSelectedOutcomeValidationKeys((currentSelectionKeys) => {
      const nextSelectionKeys = new Set(currentSelectionKeys);
      if (nextSelectionKeys.has(selectionKey)) {
        nextSelectionKeys.delete(selectionKey);
      } else {
        nextSelectionKeys.add(selectionKey);
      }
      return nextSelectionKeys;
    });
  };

  const handleRegenerateOutcomesValidations = async (
    validations: WorkstreamOutcomesValidationResponse[]
  ) => {
    if (isRegenerating || !validations.length) {
      return;
    }

    setIsRegenerating(true);
    const outcomesValidationsInBatch = validations.slice(0, READINESS_REGENERATE_BATCH_SIZE);
    setRegenerateStatus({
      requestedCount: outcomesValidationsInBatch.length,
      queuedCount: 0,
      completedCount: 0,
      settledCount: 0,
      failedToStartCount: 0,
    });
    try {
      const taskResults = await Promise.allSettled(
        outcomesValidationsInBatch.map((outcomesValidation) =>
          getService(
            AiWorkstreamOutcomesValidationService
          ).generateMetricOutcomesValidationApiV1WorkstreamMetricsWorkstreamMetricIdOutcomesValidationGeneratePost(
            Number(outcomesValidation.metric_id)
          )
        )
      );
      const pendingTasks = new Map<string, OutcomeValidationGenerationTask>();
      taskResults.forEach((taskResult, index) => {
        if (taskResult.status !== 'fulfilled') {
          return;
        }

        const outcomesValidation = outcomesValidationsInBatch[index];
        pendingTasks.set(taskResult.value.task_id, {
          taskId: taskResult.value.task_id,
          metricId: Number(outcomesValidation.metric_id),
        });
      });
      const failedToStartCount = outcomesValidationsInBatch.length - pendingTasks.size;
      let completedCount = 0;
      let settledCount = 0;
      const queuedCount = pendingTasks.size;
      setRegenerateStatus({
        requestedCount: outcomesValidationsInBatch.length,
        queuedCount,
        completedCount,
        settledCount,
        failedToStartCount,
      });

      while (pendingTasks.size > 0) {
        const statuses = await Promise.all(
          Array.from(pendingTasks.values()).map(async (task) => {
            try {
              const {status} = await getService(
                AiWorkstreamOutcomesValidationService
              ).getMetricOutcomesValidationStatusApiV1WorkstreamMetricsWorkstreamMetricIdOutcomesValidationGenerateTaskIdGet(
                task.metricId,
                task.taskId
              );
              return {task, status};
            } catch {
              return {task, status: 'failed'};
            }
          })
        );
        let hasCompletedTask = false;
        statuses.forEach(({task, status}) => {
          if (status === 'processing') {
            return;
          }

          pendingTasks.delete(task.taskId);
          settledCount += 1;
          if (status === 'completed') {
            completedCount += 1;
            hasCompletedTask = true;
          }
        });

        setRegenerateStatus({
          requestedCount: outcomesValidationsInBatch.length,
          queuedCount,
          completedCount,
          settledCount,
          failedToStartCount,
        });
        if (hasCompletedTask) {
          await queryClient.invalidateQueries({queryKey: ['workstream', 'outcomes_validations']});
        }
        if (pendingTasks.size > 0) {
          await wait(READINESS_GENERATION_STATUS_POLL_INTERVAL_MS);
        }
      }
    } finally {
      await queryClient.invalidateQueries({queryKey: ['workstream', 'outcomes_validations']});
      setIsRegenerating(false);
      setSelectedOutcomeValidationKeys(new Set());
    }
  };

  if (isLoading) {
    return (
      <Box paddingLeft="24">
        <Spinner size="small" />
      </Box>
    );
  }

  return (
    <Stack gap="16">
      <Box display="flex" alignItems="center" width="100%" style={{gap: 16}}>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Text>Score</Text>
          <CheckboxSelector
            selectedFilters={scoreFilters}
            filterOptions={OUTCOME_VALIDATION_SCORE_FILTER_OPTIONS}
            filterLabel="Score"
            onToggleFilter={handleToggleScore}
            onClearAll={() => setScoreFilters(new Set())}
          />
        </Box>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Text>Metric type</Text>
          <CheckboxSelector
            selectedFilters={metricTypeFilters}
            filterOptions={metricTypeFilterOptions}
            filterLabel="Type"
            onToggleFilter={handleToggleMetricType}
            onClearAll={() => setMetricTypeFilters(new Set())}
          />
        </Box>
        <Box marginLeft="auto" display="flex" alignItems="center" style={{gap: 12}}>
          <Text color="subtle" size="small">
            Showing {filteredOutcomesValidations.length} of {outcomesValidations.length}
          </Text>
          {canRegenerate && (
            <BulkRegenerateAction
              totalCount={outcomesValidationsToRegenerate.length}
              isRegenerating={isRegenerating}
              status={getRegenerationStatusLabel(regenerateStatus)}
              onRegenerate={() =>
                void handleRegenerateOutcomesValidations(outcomesValidationsToRegenerate)
              }
              buttonLabel={regenerateButtonLabel}
            />
          )}
        </Box>
      </Box>
      <Table columns={OUTCOME_VALIDATION_TABLE_COLUMNS} data={filteredOutcomesValidations} compact>
        <Table.Header />
        <Table.Body>
          {filteredOutcomesValidations.map(
            (outcomesValidation: WorkstreamOutcomesValidationResponse) => {
              const isValid = isValidOutcomeValidation(outcomesValidation);
              const checksForDisplay = getChecksForDisplay(
                outcomesValidation.checks ?? [],
                outcomesValidation.checks_type as WorkstreamCheckType
              );

              return (
                <Table.Row
                  key={outcomesValidation.task_id}
                  isSelectable={canRegenerate}
                  isSelected={selectedOutcomeValidationKeys.has(
                    getOutcomeValidationSelectionKey(outcomesValidation)
                  )}
                  onClick={
                    canRegenerate
                      ? () => handleToggleOutcomeValidationSelection(outcomesValidation)
                      : undefined
                  }
                >
                  <Table.Cell>
                    <Stack gap="2">
                      <Box>
                        <Text size="small">{outcomesValidation.metric_title}</Text>
                      </Box>
                      <Text color="subtle" size="xsmall">
                        Metric ID: {outcomesValidation.metric_id}
                      </Text>
                    </Stack>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="small">
                      {getOutcomeValidationMetricTypeLabel(outcomesValidation.metric_type)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {isValid && outcomesValidation.score ? (
                      <Stack gap="4">
                        <Box display="flex" alignItems="center" style={{gap: 4}}>
                          <HealthScoreIcon score={outcomesValidation.score} iconSize={20} />
                          <Text size="small">
                            {getOutcomeValidationScoreLabel(outcomesValidation.score)}
                          </Text>
                        </Box>
                        {outcomesValidation.created_at && (
                          <TaskGeneratedAtText generatedAt={outcomesValidation.created_at} />
                        )}
                      </Stack>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    {isValid && (
                      <HealthScoreChecks
                        entityId={Number(outcomesValidation.metric_id)}
                        checks={checksForDisplay.map((check) => ({
                          ...check,
                          failureCopy: undefined,
                        }))}
                        checksType={outcomesValidation.checks_type as WorkstreamCheckType}
                        iconGap={4}
                        useFilledIcon
                        showCheckRationale
                        showFailingChecksFirst
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            }
          )}
        </Table.Body>
      </Table>
    </Stack>
  );
};

export const tableColumns = [
  {
    type: 'workstream',
    minWidth: 325,
  },
  {
    type: 'Pillar Level',
    minWidth: 125,
  },
  {
    type: 'Score',
    minWidth: 200,
  },
  {
    type: 'Checks',
    minWidth: 350,
  },
];

type HealthScoreFilter = 'not_generated' | 'poor' | 'adequate' | 'exemplary';
const PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE = 'Unavailable';
type PillarLevelFilter = number | typeof PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE;

const SCORE_FILTER_OPTIONS: Array<{label: string; value: HealthScoreFilter}> = [
  {label: 'Poor', value: 'poor'},
  {label: 'Adequate', value: 'adequate'},
  {label: 'Exemplary', value: 'exemplary'},
  {label: 'Not generated', value: 'not_generated'},
];

const HEALTH_SCORE_STATUS_MAX_FAILURES = 3;
const HEALTH_SCORE_COMPLETED_STATUS = 'completed';
const HEALTH_SCORE_PROCESSING_STATUS = 'processing';
const HEALTH_SCORE_EXPORT_FILENAME_FALLBACK = 'CompanyOS-Workstream-Health-Scores.csv';

type HealthScoreGenerationProgress = {
  completedCount: number;
  settledCount: number;
  totalCount: number;
};

type WorkstreamHealthScoresCsvExportRequest = {
  checks_type: 'default';
  timeframe?: WorkstreamTimeframe;
  score?: string[];
  workstream_ids: number[];
};

const ALL_TIMEFRAMES_LABEL = 'All timeframes';

const PILLAR_LEVEL_LABELS: Record<number, string> = {
  1: 'Pillar workstream',
  2: 'Narrative workstream',
  3: 'Subworkstream of narrative workstream',
};
const PILLAR_LEVEL_FILTER_LABELS: Record<number, string> = {
  1: 'Pillar Workstream',
  2: 'Narrative Workstream',
  3: 'Subworkstream',
};
const PILLAR_LEVEL_UNAVAILABLE_LABEL = 'Level unavailable';

const getPillarLevelLabel = (pillarLevel: number, pillar?: string | null) => {
  const pillarLevelLabel = PILLAR_LEVEL_LABELS[pillarLevel];
  if (!pillarLevelLabel) {
    return pillar
      ? `Nested subworkstream at level ${pillarLevel} under ${pillar}`
      : `Nested subworkstream at level ${pillarLevel}`;
  }
  return pillar ? `${pillarLevelLabel} under ${pillar}` : pillarLevelLabel;
};

const getPillarLevelHeading = (pillarLevel: number) => `Level ${pillarLevel}`;

const getPillarLevelFilterValue = (
  healthScore: WorkstreamHealthScoreResponse
): PillarLevelFilter => {
  const pillarLevel = healthScore.workstream_pillar_metadata?.pillar_level;
  return typeof pillarLevel === 'number' && pillarLevel > 0
    ? pillarLevel
    : PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE;
};

const getPillarLevelFilterLabel = (pillarLevelFilter: PillarLevelFilter) =>
  pillarLevelFilter === PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE
    ? PILLAR_LEVEL_UNAVAILABLE_LABEL
    : `${pillarLevelFilter} (${PILLAR_LEVEL_FILTER_LABELS[pillarLevelFilter]})`;

const getPillarLevelFilterOptions = (
  healthScores: WorkstreamHealthScoreResponse[]
): Array<{label: string; value: PillarLevelFilter}> => {
  const selectedFilters = new Set<PillarLevelFilter>(
    healthScores.map((healthScore) => getPillarLevelFilterValue(healthScore))
  );
  const pillarLevels = Array.from(selectedFilters).filter(
    (pillarLevelFilter): pillarLevelFilter is number =>
      pillarLevelFilter !== PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE
  );
  const options: Array<{label: string; value: PillarLevelFilter}> = pillarLevels
    .sort((a, b) => a - b)
    .map((pillarLevelFilter) => ({
      label: getPillarLevelFilterLabel(pillarLevelFilter),
      value: pillarLevelFilter,
    }));

  if (selectedFilters.has(PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE)) {
    options.push({
      label: PILLAR_LEVEL_UNAVAILABLE_LABEL,
      value: PILLAR_LEVEL_UNAVAILABLE_FILTER_VALUE,
    });
  }

  return options;
};

const getFilterTriggerLabel = <T extends string>(
  selectedFilters: Set<T>,
  filterOptions: Array<{label: string; value: T}>,
  filterLabel: string
) => {
  if (selectedFilters.size === 0) {
    return `All ${filterLabel.toLowerCase()}s`;
  }

  if (selectedFilters.size === 1) {
    const selectedFilter = filterOptions.find((option) => selectedFilters.has(option.value));
    return selectedFilter?.label ?? `1 ${filterLabel.toLowerCase()}`;
  }

  return `${selectedFilters.size} ${filterLabel.toLowerCase()}s`;
};

const getPillarLevelFilterTriggerLabel = (pillarLevelFilters: Set<PillarLevelFilter>) => {
  if (pillarLevelFilters.size === 0) {
    return 'All levels';
  }

  if (pillarLevelFilters.size === 1) {
    return getPillarLevelFilterLabel(Array.from(pillarLevelFilters)[0]);
  }

  return `${pillarLevelFilters.size} levels selected`;
};

const wait = (durationMs: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, durationMs);
  });

const buildWorkstreamHealthScoresCsvExportRequest = ({
  timeframe,
  workstreamIds,
}: {
  timeframe: WorkstreamTimeframe | undefined;
  workstreamIds: number[];
}): WorkstreamHealthScoresCsvExportRequest => {
  return {
    checks_type: 'default',
    ...(timeframe ? {timeframe} : {}),
    workstream_ids: workstreamIds,
  };
};

const getCsvFilename = (response: Response) => {
  const contentDisposition = response.headers.get('Content-Disposition');
  const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/i);
  return filenameMatch?.[1] ?? HEALTH_SCORE_EXPORT_FILENAME_FALLBACK;
};

const downloadCsvBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const exportWorkstreamHealthScoresCsv = async (
  requestBody: WorkstreamHealthScoresCsvExportRequest
) => {
  const response = await fetch(`${getBackendUrl()}/api/v1/workstream/health_scores/csv`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  downloadCsvBlob(await response.blob(), getCsvFilename(response));
};

const getGeneratedTaskIds = (
  taskResults: Array<PromiseSettledResult<WorkstreamHealthScoreTaskResponse>>
) =>
  Array.from(
    new Set(
      taskResults.flatMap((taskResult) =>
        taskResult.status === 'fulfilled' ? [taskResult.value.task_id] : []
      )
    )
  );

const getHealthScoreGenerationStatus = async (taskId: string) => {
  try {
    const {status} = await getService(
      AiWorkstreamHealthScoreService
    ).getWorkstreamHealthScoreStatusApiV1WorkstreamHealthScoreGenerateTaskIdGet(taskId);
    return {taskId, status};
  } catch {
    return {taskId, status: undefined};
  }
};

const waitForHealthScoreGenerationTasks = async ({
  taskIds,
  onCompletedTasks,
  onProgress,
}: {
  taskIds: string[];
  onCompletedTasks: () => Promise<void>;
  onProgress: (progress: HealthScoreGenerationProgress) => void;
}) => {
  const pendingTaskIds = new Set(taskIds);
  const completedTaskIds = new Set<string>();
  const settledTaskIds = new Set<string>();
  const failedPollCounts = new Map<string, number>();

  while (pendingTaskIds.size > 0) {
    const previousCompletedCount = completedTaskIds.size;
    const taskStatuses = await Promise.all(
      Array.from(pendingTaskIds).map((taskId) => getHealthScoreGenerationStatus(taskId))
    );

    taskStatuses.forEach(({taskId, status}) => {
      if (!status) {
        const failedPollCount = (failedPollCounts.get(taskId) ?? 0) + 1;

        if (failedPollCount >= HEALTH_SCORE_STATUS_MAX_FAILURES) {
          settledTaskIds.add(taskId);
          pendingTaskIds.delete(taskId);
          failedPollCounts.delete(taskId);
          return;
        }

        failedPollCounts.set(taskId, failedPollCount);
        return;
      }

      failedPollCounts.delete(taskId);
      if (status !== HEALTH_SCORE_PROCESSING_STATUS) {
        settledTaskIds.add(taskId);
        if (status === HEALTH_SCORE_COMPLETED_STATUS) {
          completedTaskIds.add(taskId);
        }
        pendingTaskIds.delete(taskId);
      }
    });

    onProgress({
      completedCount: completedTaskIds.size,
      settledCount: settledTaskIds.size,
      totalCount: taskIds.length,
    });

    if (completedTaskIds.size > previousCompletedCount) {
      await onCompletedTasks();
    }

    if (pendingTaskIds.size > 0) {
      await wait(READINESS_GENERATION_STATUS_POLL_INTERVAL_MS);
    }
  }
};

const SegmentLabelWithClear = ({
  label,
  canClearAll,
  onClearAll,
}: {
  label: string;
  canClearAll: boolean;
  onClearAll: () => void;
}) => {
  const handleClearAllClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    onClearAll();
  };

  return (
    <Box display="flex" alignItems="center" style={{width: '100%'}}>
      <Box
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <Text isBold size="small">
          {label}
        </Text>
      </Box>
      {canClearAll && (
        <Box style={{marginLeft: 16, flexShrink: 0}}>
          <Button variant="transparent" size="small" onClick={handleClearAllClick}>
            Clear all
          </Button>
        </Box>
      )}
    </Box>
  );
};

const CheckboxSelector = <T extends string>({
  selectedFilters,
  filterOptions,
  filterLabel,
  onToggleFilter,
  onClearAll,
}: {
  selectedFilters: Set<T>;
  filterOptions: Array<{label: string; value: T}>;
  filterLabel: string;
  onToggleFilter: (filter: T) => void;
  onClearAll: () => void;
}) => {
  const hasActiveFilter = selectedFilters.size > 0;

  return (
    <Menu.Wrapper closeOnSelection={false}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button variant="outline" withDropdownIcon {...getTriggerProps()}>
            {getFilterTriggerLabel(selectedFilters, filterOptions, filterLabel)}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-start" style={{minWidth: 220}}>
            <Menu.Segment
              withLabel={
                <SegmentLabelWithClear
                  label={filterLabel}
                  canClearAll={hasActiveFilter}
                  onClearAll={onClearAll}
                />
              }
            >
              {filterOptions.map((option) => (
                <Menu.ActionItem
                  key={option.value}
                  onClick={() => onToggleFilter(option.value)}
                  withLeftAccessory={
                    <Checkbox checked={selectedFilters.has(option.value)} readOnly tabIndex={-1} />
                  }
                >
                  {option.label}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const PillarLevelCheckboxSelector = ({
  pillarLevelFilters,
  pillarLevelFilterOptions,
  onTogglePillarLevel,
  onClearAll,
}: {
  pillarLevelFilters: Set<PillarLevelFilter>;
  pillarLevelFilterOptions: Array<{label: string; value: PillarLevelFilter}>;
  onTogglePillarLevel: (pillarLevelFilter: PillarLevelFilter) => void;
  onClearAll: () => void;
}) => {
  const hasActiveFilter = pillarLevelFilters.size > 0;

  return (
    <Menu.Wrapper closeOnSelection={false}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button variant="outline" withDropdownIcon {...getTriggerProps()}>
            {getPillarLevelFilterTriggerLabel(pillarLevelFilters)}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-start" style={{minWidth: 220}}>
            <Menu.Segment
              withLabel={
                <SegmentLabelWithClear
                  label="Pillar Level"
                  canClearAll={hasActiveFilter}
                  onClearAll={onClearAll}
                />
              }
            >
              {pillarLevelFilterOptions.map((option) => (
                <Menu.ActionItem
                  key={option.value}
                  onClick={() => onTogglePillarLevel(option.value)}
                  withLeftAccessory={
                    <Checkbox
                      checked={pillarLevelFilters.has(option.value)}
                      readOnly
                      tabIndex={-1}
                    />
                  }
                >
                  {option.label}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const TimeframeSelector = ({
  timeframe,
  onTimeframeChange,
}: {
  timeframe: WorkstreamTimeframe | undefined;
  onTimeframeChange: (timeframe: WorkstreamTimeframe | undefined) => void;
}) => {
  const timeframes = useTimeframes();
  const timeframeOptions: Array<{label: string; value: WorkstreamTimeframe | undefined}> = [
    {label: ALL_TIMEFRAMES_LABEL, value: undefined},
    ...timeframes.map(({name}) => ({label: name, value: name})),
  ];
  const triggerLabel =
    timeframeOptions.find((option) => option.value === timeframe)?.label ?? ALL_TIMEFRAMES_LABEL;

  return (
    <Menu.Wrapper>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button variant="outline" withDropdownIcon {...getTriggerProps()}>
            {triggerLabel}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-start" style={{minWidth: 180}}>
            <Menu.Segment withLabel="Timeframe">
              {timeframeOptions.map((option) => (
                <Menu.ActionItem
                  key={option.value ?? 'all-timeframes'}
                  onClick={() => onTimeframeChange(option.value)}
                  withLeftAccessory={
                    <RadioButton checked={timeframe === option.value} readOnly tabIndex={-1} />
                  }
                >
                  {option.label}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const ViewHealthScores = ({
  timeframe,
  onTimeframeChange,
  canRegenerate,
}: {
  timeframe: WorkstreamTimeframe | undefined;
  onTimeframeChange: (timeframe: WorkstreamTimeframe | undefined) => void;
  canRegenerate: boolean;
}) => {
  const [scoreFilters, setScoreFilters] = useState<Set<HealthScoreFilter>>(new Set());
  const [pillarLevelFilters, setPillarLevelFilters] = useState<Set<PillarLevelFilter>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);
  const [selectedWorkstreamIds, setSelectedWorkstreamIds] = useState<Set<number>>(new Set());
  const [regenerateStatus, setRegenerateStatus] = useState<RegenerationStatus | undefined>(
    undefined
  );
  const isRegeneratingRef = useRef(false);

  const {healthScores = [], isLoading} = useWorkstreamHealthScores({
    checksType: 'default',
    timeframe,
  });
  let sortedHealthScores = useMemo(
    () => sortHealthScoresByWorkstreamName(healthScores),
    [healthScores]
  );
  const pillarLevelFilterOptions = useMemo(
    () => getPillarLevelFilterOptions(sortedHealthScores),
    [sortedHealthScores]
  );
  sortedHealthScores = sortedHealthScores.filter(
    (healthScore) =>
      pillarLevelFilters.size === 0 ||
      pillarLevelFilters.has(getPillarLevelFilterValue(healthScore))
  );
  const filteredHealthScores = sortedHealthScores.filter((healthScore) => {
    if (scoreFilters.size === 0) {
      return true;
    }

    if (!healthScore.task_id && scoreFilters.has('not_generated')) {
      return true;
    }

    return scoreFilters.has(healthScore.score?.toLowerCase() as HealthScoreFilter);
  });
  const visibleWorkstreamIds = Array.from(
    new Set(
      filteredHealthScores
        .map((healthScore) => healthScore.workstream?.id)
        .filter((workstreamId): workstreamId is number => !!workstreamId)
    )
  );
  const allWorkstreamIds = Array.from(
    new Set(
      healthScores
        .map((healthScore) => healthScore.workstream?.id)
        .filter((workstreamId): workstreamId is number => !!workstreamId)
    )
  );
  const selectedVisibleWorkstreamIds = visibleWorkstreamIds.filter((workstreamId) =>
    selectedWorkstreamIds.has(workstreamId)
  );
  const workstreamIdsToRegenerate = selectedVisibleWorkstreamIds.length
    ? selectedVisibleWorkstreamIds
    : visibleWorkstreamIds;
  const regenerateButtonLabel = selectedVisibleWorkstreamIds.length
    ? `Regenerate selected (${selectedVisibleWorkstreamIds.length})`
    : undefined;
  const regenerateStatusLabel = getRegenerationStatusLabel(regenerateStatus);

  const handleToggleScore = (scoreFilter: HealthScoreFilter) => {
    setScoreFilters((currentScoreFilters) => {
      const nextScoreFilters = new Set(currentScoreFilters);
      if (nextScoreFilters.has(scoreFilter)) {
        nextScoreFilters.delete(scoreFilter);
      } else {
        nextScoreFilters.add(scoreFilter);
      }
      return nextScoreFilters;
    });
  };

  const handleTogglePillarLevel = (pillarLevelFilter: PillarLevelFilter) => {
    setPillarLevelFilters((currentPillarLevelFilters) => {
      const nextPillarLevelFilters = new Set(currentPillarLevelFilters);
      if (nextPillarLevelFilters.has(pillarLevelFilter)) {
        nextPillarLevelFilters.delete(pillarLevelFilter);
      } else {
        nextPillarLevelFilters.add(pillarLevelFilter);
      }
      return nextPillarLevelFilters;
    });
  };

  const handleToggleWorkstreamSelection = (workstreamId: number) => {
    setSelectedWorkstreamIds((currentSelectedWorkstreamIds) => {
      const nextSelectedWorkstreamIds = new Set(currentSelectedWorkstreamIds);
      if (nextSelectedWorkstreamIds.has(workstreamId)) {
        nextSelectedWorkstreamIds.delete(workstreamId);
      } else {
        nextSelectedWorkstreamIds.add(workstreamId);
      }
      return nextSelectedWorkstreamIds;
    });
  };

  const handleExport = async () => {
    if (isExporting || allWorkstreamIds.length === 0) {
      return;
    }

    setIsExporting(true);
    setExportError(undefined);
    try {
      await exportWorkstreamHealthScoresCsv(
        buildWorkstreamHealthScoresCsvExportRequest({
          timeframe,
          workstreamIds: allWorkstreamIds,
        })
      );
    } catch {
      setExportError('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRegenerateWorkstreams = async (workstreamIds: number[]) => {
    if (isRegeneratingRef.current || !workstreamIds.length) {
      return;
    }

    isRegeneratingRef.current = true;
    const workstreamIdsInBatch = workstreamIds.slice(0, READINESS_REGENERATE_BATCH_SIZE);
    setIsRegenerating(true);
    setRegenerateStatus({
      requestedCount: workstreamIdsInBatch.length,
      queuedCount: 0,
      completedCount: 0,
      settledCount: 0,
      failedToStartCount: 0,
    });
    try {
      const refreshHealthScores = () =>
        queryClient.invalidateQueries({
          queryKey: ['workstream', 'health_scores', 'default', timeframe],
        });
      const taskResults = await Promise.allSettled(
        workstreamIdsInBatch.map((workstreamId) =>
          getService(
            AiWorkstreamHealthScoreService
          ).generateWorkstreamHealthScoreApiV1WorkstreamHealthScoreGeneratePost({
            workstream_id: workstreamId,
            checks_type: 'default',
            timeframe,
          })
        )
      );

      await refreshHealthScores();
      const taskIds = getGeneratedTaskIds(taskResults);
      setRegenerateStatus((currentStatus) =>
        currentStatus
          ? {
              ...currentStatus,
              queuedCount: taskIds.length,
              failedToStartCount: workstreamIdsInBatch.length - taskIds.length,
            }
          : currentStatus
      );
      await waitForHealthScoreGenerationTasks({
        taskIds,
        onCompletedTasks: refreshHealthScores,
        onProgress: ({completedCount, settledCount}) => {
          setRegenerateStatus((currentStatus) =>
            currentStatus ? {...currentStatus, completedCount, settledCount} : currentStatus
          );
        },
      });
    } finally {
      isRegeneratingRef.current = false;
      setIsRegenerating(false);
      setRegenerateStatus(undefined);
      setSelectedWorkstreamIds(new Set());
    }
  };

  if (isLoading) {
    return (
      <Box paddingLeft="24">
        <Spinner size="small" />
      </Box>
    );
  }

  return (
    <Stack gap="16">
      <Box display="flex" alignItems="center" width="100%" style={{gap: 16, flexWrap: 'wrap'}}>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Text>Score</Text>
          <CheckboxSelector
            selectedFilters={scoreFilters}
            filterOptions={SCORE_FILTER_OPTIONS}
            filterLabel="Score"
            onToggleFilter={handleToggleScore}
            onClearAll={() => setScoreFilters(new Set())}
          />
        </Box>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Text>Pillar level</Text>
          <PillarLevelCheckboxSelector
            pillarLevelFilters={pillarLevelFilters}
            pillarLevelFilterOptions={pillarLevelFilterOptions}
            onTogglePillarLevel={handleTogglePillarLevel}
            onClearAll={() => setPillarLevelFilters(new Set())}
          />
        </Box>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Text>Timeframe</Text>
          <TimeframeSelector timeframe={timeframe} onTimeframeChange={onTimeframeChange} />
        </Box>
        <Box
          marginLeft="auto"
          display="flex"
          alignItems="center"
          style={{gap: 12, flexWrap: 'wrap'}}
        >
          <Text color="subtle" size="small">
            Showing {filteredHealthScores.length} of {healthScores.length}
          </Text>
          {canRegenerate && (
            <BulkRegenerateAction
              totalCount={workstreamIdsToRegenerate.length}
              isRegenerating={isRegenerating}
              status={regenerateStatusLabel}
              onRegenerate={() => void handleRegenerateWorkstreams(workstreamIdsToRegenerate)}
              buttonLabel={regenerateButtonLabel}
            />
          )}
          <Button
            variant="primary"
            onClick={() => void handleExport()}
            disabled={allWorkstreamIds.length === 0 || isExporting}
            isLoading={isExporting}
          >
            Export all{allWorkstreamIds.length > 0 ? ` (${allWorkstreamIds.length})` : ''}
          </Button>
          {exportError && (
            <Text color="error" size="small">
              {exportError}
            </Text>
          )}
        </Box>
      </Box>

      <Table columns={tableColumns} data={filteredHealthScores} compact>
        <Table.Header />

        <Table.Body>
          {filteredHealthScores.map((healthScore) => {
            const workstreamId = healthScore.workstream?.id;

            if (!workstreamId) {
              return null;
            }

            return (
              <WorkstreamHealthScoreRow
                key={`health-score__${workstreamId}__${healthScore.task_id ?? 'pending'}`}
                healthScore={healthScore}
                canSelect={canRegenerate}
                isSelected={selectedWorkstreamIds.has(workstreamId)}
                onToggleSelection={handleToggleWorkstreamSelection}
              />
            );
          })}
        </Table.Body>
      </Table>
    </Stack>
  );
};

const getEnabledWorkstreamsFromHealthScores = (
  healthScores: WorkstreamHealthScoreResponse[]
): WorkstreamBasic[] => {
  const seenWorkstreamIds = new Set<number>();
  const workstreams: WorkstreamBasic[] = [];

  healthScores.forEach(({workstream}) => {
    if (!workstream || seenWorkstreamIds.has(workstream.id)) {
      return;
    }

    seenWorkstreamIds.add(workstream.id);
    workstreams.push(workstream);
  });

  return workstreams.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: 'base'})
  );
};

const sortHealthScoresByWorkstreamName = (
  healthScores: WorkstreamHealthScoreResponse[]
): WorkstreamHealthScoreResponse[] => {
  return [...healthScores].sort((a, b) =>
    (a.workstream?.name ?? '').localeCompare(b.workstream?.name ?? '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
};

const PillarLevelCell = ({
  pillarLevel,
  pillar,
}: {
  pillarLevel?: number | null;
  pillar?: string | null;
}) => {
  if (!pillarLevel) {
    return (
      <Text color="subtle" size="small">
        {PILLAR_LEVEL_UNAVAILABLE_LABEL}
      </Text>
    );
  }

  const label = getPillarLevelLabel(pillarLevel, pillar);
  return (
    <Stack gap="4">
      <Text size="small">{getPillarLevelHeading(pillarLevel)}</Text>
      <Box>
        <Text color="subtle" size="xsmall">
          {label}
        </Text>
      </Box>
    </Stack>
  );
};

const TaskGeneratedAtText = ({generatedAt}: {generatedAt: string | null | undefined}) => {
  return (
    generatedAt && (
      <Box marginLeft="24" style={{marginTop: '-2px'}}>
        <Text color="subtle" size="xsmall">
          Last generated {formatGeneratedAt(generatedAt)}
        </Text>
      </Box>
    )
  );
};

const WorkstreamHealthScoreRow = ({
  healthScore,
  canSelect,
  isSelected,
  onToggleSelection,
}: {
  healthScore: WorkstreamHealthScoreResponse;
  canSelect: boolean;
  isSelected: boolean;
  onToggleSelection: (workstreamId: number) => void;
}) => {
  const {workstream} = healthScore;

  if (!workstream?.id) {
    return null;
  }

  const workstreamId = workstream.id;

  return (
    <Table.Row
      key={`health-score__${workstreamId}__${healthScore.task_id ?? 'pending'}`}
      isSelectable={canSelect}
      isSelected={isSelected}
      onClick={canSelect ? () => onToggleSelection(workstreamId) : undefined}
    >
      <Table.Cell>
        <Text size="small">
          <Link to={`/workstreams/${workstreamId}`} target="_blank">
            <Truncate lines={1} tooltipControlProps={{placement: 'top-start'}}>
              {workstream.name}
            </Truncate>
          </Link>
        </Text>
      </Table.Cell>
      <Table.Cell>
        <PillarLevelCell
          pillarLevel={healthScore.workstream_pillar_metadata?.pillar_level}
          pillar={healthScore.workstream_pillar_metadata?.pillar_workstream_name}
        />
      </Table.Cell>
      <Table.Cell>
        {healthScore.score && (
          <Box display="flex" alignItems="flex-start" style={{gap: 4}}>
            <HealthScoreResults
              workstreamId={workstreamId}
              score={healthScore.score}
              checks={healthScore.checks ?? []}
              iconSize={20}
              showText
              style={{gap: 4}}
              showCheckRationale
              source="health_score_dashboard"
              showFailingChecksFirst
            />
            <Tooltip title={healthScore.rationale} placement="top">
              <Box style={{height: 16}}>
                <UIIcon src={InfoLine} size="small" />
              </Box>
            </Tooltip>
          </Box>
        )}
        {healthScore.generated_at && <TaskGeneratedAtText generatedAt={healthScore.generated_at} />}
      </Table.Cell>
      <Table.Cell>
        <HealthScoreChecks
          entityId={workstreamId}
          checks={healthScore.checks ?? []}
          iconGap={4}
          useFilledIcon
          showFailingChecksFirst
        />
      </Table.Cell>
    </Table.Row>
  );
};

const WorkstreamSearchInput = ({
  workstreams,
  selectedWorkstream,
  setSelectedWorkstreamId,
}: {
  workstreams: WorkstreamBasic[];
  selectedWorkstream: WorkstreamBasic | undefined;
  setSelectedWorkstreamId: (workstreamId: number) => void;
}) => {
  const selectedWorkstreamId = selectedWorkstream?.id;
  const [inputValue, setInputValue] = useState(selectedWorkstream?.name ?? '');
  const inputContainerRef = useRef<HTMLElement | null>(null);
  const isTypeaheadClick = useRef(false);
  const normalizedInput = inputValue.trim().toLowerCase();
  const shouldShowAllResults = !normalizedInput || inputValue === selectedWorkstream?.name;
  const filteredWorkstreams = shouldShowAllResults
    ? workstreams
    : workstreams.filter((workstream) => workstream.name.toLowerCase().includes(normalizedInput));

  useEffect(() => {
    setInputValue(selectedWorkstream?.name ?? '');
  }, [selectedWorkstream]);

  const handleSelection = (workstream: WorkstreamBasic) => {
    setSelectedWorkstreamId(workstream.id);
    setInputValue(workstream.name);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  };

  const handleInputBlur = () => {
    if (isTypeaheadClick.current) {
      isTypeaheadClick.current = false;
      return;
    }

    setInputValue(selectedWorkstream?.name ?? '');
  };

  const handleKeyDown: React.KeyboardEventHandler = (e) => {
    e.stopPropagation();
  };

  const renderTypeaheadRow = (workstream: WorkstreamBasic) => (
    <Typeahead.Row
      key={workstream.id}
      onMouseDown={() => (isTypeaheadClick.current = true)}
      value={workstream}
      withTitle={
        <Text isBold={selectedWorkstreamId === workstream.id}>
          <Box whiteSpace="nowrap" overflow="hidden" style={{textOverflow: 'ellipsis'}}>
            {workstream.name}
          </Box>
        </Text>
      }
      withRightAccessory={selectedWorkstreamId === workstream.id && <UIIcon src={CheckmarkLine} />}
    />
  );

  const renderNoResultsRow = () => (
    <Typeahead.Row disabled>
      <Box paddingLeft="Micro Medium">
        <Text>{t('nothing_found')}</Text>
      </Box>
    </Typeahead.Row>
  );

  return (
    <Split gap="12" alignY="center">
      <Box>
        <Text isBold>Select workstream: </Text>
      </Box>
      <Box width="100%" style={{maxWidth: 520}}>
        <Typeahead.Wrapper
          isPortaled={false}
          onSelection={handleSelection}
          onBlur={handleInputBlur}
        >
          {({getContentProps, getTriggerProps}) => (
            <>
              <TextInput.Container ref={inputContainerRef}>
                <TextInput.Accessory>
                  <UIIcon src={SearchLine} />
                </TextInput.Accessory>
                <TextInput.Input
                  placeholder={t('select_workstream')}
                  value={inputValue}
                  {...getTriggerProps({
                    onChange: handleInputChange,
                    onKeyDown: handleKeyDown,
                  })}
                />
              </TextInput.Container>
              <Typeahead.Container {...getContentProps()} triggerRef={inputContainerRef}>
                {filteredWorkstreams.length ? (
                  <Typeahead.Results
                    results={filteredWorkstreams}
                    renderRow={renderTypeaheadRow}
                    initialResults={12}
                    maxResults={filteredWorkstreams.length}
                  />
                ) : (
                  <Typeahead.Results results={[{}]} renderRow={renderNoResultsRow} />
                )}
              </Typeahead.Container>
            </>
          )}
        </Typeahead.Wrapper>
      </Box>
    </Split>
  );
};

const GenerateToolContent = ({
  taskData,
  generateTaskId,
  generate,
  isGenerating,
  workstreams,
  selectedWorkstream,
  setSelectedWorkstreamId,
  isSubWorkstreamContextLoading,
  subWorkstreamContext,
  subWorkstreamContextError,
}: {
  taskData: {
    task_id: string | undefined;
    generated_at: string | null | undefined;
    checks: WorkstreamCheckResponse[];
  };
  generateTaskId: string | undefined;
  generate: () => void | Promise<void>;
  isGenerating: boolean;
  workstreams: WorkstreamBasic[];
  selectedWorkstream: WorkstreamBasic | undefined;
  setSelectedWorkstreamId: (workstreamId: number) => void;
  isSubWorkstreamContextLoading: boolean;
  subWorkstreamContext: string | undefined;
  subWorkstreamContextError: Error | null | undefined;
}) => {
  const [taskId, setTaskId] = useState<string>();
  const selectedWorkstreamId = selectedWorkstream?.id;

  useEffect(() => {
    setTaskId(taskData?.task_id);
  }, [taskData?.task_id]);

  useEffect(() => {
    if (generateTaskId) {
      setTaskId(generateTaskId);
    }
  }, [generateTaskId]);

  return (
    <Stack gap="12">
      <WorkstreamSearchInput
        workstreams={workstreams}
        selectedWorkstream={selectedWorkstream}
        setSelectedWorkstreamId={setSelectedWorkstreamId}
      />

      {selectedWorkstreamId && (
        <Stack gap="16">
          <Box>
            <Text>
              Workstream URL:{' '}
              <Link to={`/workstreams/${selectedWorkstreamId}`} target="_blank">
                {`${getFrontendUrl()}workstreams/${selectedWorkstreamId}`}
              </Link>
            </Text>
          </Box>
          <Box>
            <Text>
              Latest Task ID:{' '}
              {isGenerating ? (
                'Generating…'
              ) : taskId ? (
                taskId
              ) : (
                <Text color="error">Please generate a task ID.</Text>
              )}
            </Text>
          </Box>

          <Split gap="12">
            <Button
              variant="primary"
              onClick={() => void generate()}
              disabled={isGenerating}
              isLoading={isGenerating}
            >
              {taskId ? t('regenerate') : t('generate')}
            </Button>
          </Split>
        </Stack>
      )}

      {!!taskData?.checks?.length && (
        <Box style={{marginTop: '36px'}}>
          <Stack gap="12">
            <Text isBold>Workstream Checks:</Text>
            {isGenerating ? (
              <Box>
                <Spinner size="small" />
              </Box>
            ) : (
              taskData.checks.map((check, i) => (
                <Box key={check.id}>
                  <Text>
                    {i + 1}. {check.id}:{' '}
                  </Text>
                  <Text color={check.passed ? 'standard' : 'error'}>
                    {check.passed ? 'Passed' : 'Failed'}.{' '}
                  </Text>
                  <Text color="subtle">{check.rationale ?? getFailureCopy(check.id)}</Text>
                </Box>
              ))
            )}
          </Stack>
        </Box>
      )}

      <Box style={{marginTop: '36px'}}>
        {isSubWorkstreamContextLoading ? (
          <Spinner size="small" />
        ) : subWorkstreamContext ? (
          <Stack gap="12">
            <Text isBold>Workstream Context:</Text>
            <RichTextArea value={subWorkstreamContext} />
          </Stack>
        ) : subWorkstreamContextError ? (
          <Text color="error">{subWorkstreamContextError.message}</Text>
        ) : null}
      </Box>
    </Stack>
  );
};

export const GenerateHealthScoreTool = ({
  checksType,
  timeframe,
}: {
  checksType: 'default' | 'oceo';
  timeframe: string | undefined;
}) => {
  const [selectedWorkstreamId, setSelectedWorkstreamId] = useState<number>();
  const {healthScores = [], isLoading: isHealthScoresLoading} = useWorkstreamHealthScores({
    checksType,
    timeframe,
  });
  const workstreams = useMemo(
    () => getEnabledWorkstreamsFromHealthScores(healthScores),
    [healthScores]
  );
  const selectedWorkstream =
    workstreams.find((workstream) => workstream.id === selectedWorkstreamId) ?? workstreams[0];
  const activeWorkstreamId = selectedWorkstream?.id;
  const healthScore = healthScores.find((score) => score.workstream?.id === activeWorkstreamId);

  if (isHealthScoresLoading) {
    return <Spinner size="small" />;
  }

  if (!workstreams.length) {
    return <Text>No enabled workstreams available.</Text>;
  }

  return (
    <GenerateHealthScoreForSelectedWorkstream
      key={`${activeWorkstreamId}-${checksType}-${timeframe ?? 'all-timeframes'}`}
      checksType={checksType}
      timeframe={timeframe}
      healthScore={healthScore}
      workstreams={workstreams}
      selectedWorkstream={selectedWorkstream}
      setSelectedWorkstreamId={setSelectedWorkstreamId}
    />
  );
};

const GenerateHealthScoreForSelectedWorkstream = ({
  checksType,
  timeframe,
  healthScore,
  workstreams,
  selectedWorkstream,
  setSelectedWorkstreamId,
}: {
  checksType: 'default' | 'oceo';
  timeframe: string | undefined;
  healthScore: WorkstreamHealthScoreResponse | undefined;
  workstreams: WorkstreamBasic[];
  selectedWorkstream: WorkstreamBasic;
  setSelectedWorkstreamId: (workstreamId: number) => void;
}) => {
  const handleGenerationComplete = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['workstream', 'health_scores', checksType, timeframe],
    });
  }, [checksType, timeframe]);

  const {generateTaskId, generate, isGenerating} = useWorkstreamHealthScoreGenerate({
    workstreamId: selectedWorkstream.id,
    checksType,
    timeframe,
    onComplete: handleGenerationComplete,
  });

  const {
    context: subWorkstreamContext,
    isLoading: isSubWorkstreamContextLoading,
    error: subWorkstreamContextError,
  } = useWorkstreamHealthScoreContext(checksType, selectedWorkstream.id, timeframe);

  return (
    <GenerateToolContent
      taskData={{
        task_id: healthScore?.task_id,
        generated_at: healthScore?.generated_at,
        checks: healthScore?.checks ?? [],
      }}
      generateTaskId={generateTaskId}
      generate={generate}
      isGenerating={isGenerating}
      workstreams={workstreams}
      selectedWorkstream={selectedWorkstream}
      setSelectedWorkstreamId={setSelectedWorkstreamId}
      isSubWorkstreamContextLoading={isSubWorkstreamContextLoading}
      subWorkstreamContext={subWorkstreamContext}
      subWorkstreamContextError={subWorkstreamContextError}
    />
  );
};
